import React, { useState } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";

const EditEnrollment = ({ plansData, originalPlansData, setPlansData }) => {
  const [enrollmentData, setEnrollmentData] = useState({});
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isCustom, setIsCustom] = useState(false);

  // Calculate the maximum number of plans across all datasets
  const maxPlans = Math.max(
    ...plansData.map((dataset) => dataset.plans.length)
  );

  // Handle enrollment data option change
  const handleEnrollmentDataChange = (e) => {
    if (e.target.value === 'custom') {
      setIsCustom(true);
      setIsCollapsed(false);
    } else if (e.target.value !== '') {
      setIsCustom(false);
      const newEnrollmentData = {};
      const selectedIndex = parseInt(e.target.value);

      // Check if the selected dataset exists in originalPlansData
      if (originalPlansData[selectedIndex] && originalPlansData[selectedIndex].plans) {
        originalPlansData[selectedIndex].plans.forEach((plan, planIndex) => {
          newEnrollmentData[`tier_${planIndex}`] = {
            individual: plan.enrollment.individual || 0,
            individualAndSpouse: plan.enrollment.individualAndSpouse || 0,
            individualAndChildren: plan.enrollment.individualAndChildren || 0,
            family: plan.enrollment.family || 0,
          };
        });
      } else {
        // If original data is not available, use data from plansData as fallback
        if (plansData[selectedIndex] && plansData[selectedIndex].plans) {
          plansData[selectedIndex].plans.forEach((plan, planIndex) => {
            newEnrollmentData[`tier_${planIndex}`] = {
              individual: plan.enrollment.individual || 0,
              individualAndSpouse: plan.enrollment.individualAndSpouse || 0,
              individualAndChildren: plan.enrollment.individualAndChildren || 0,
              family: plan.enrollment.family || 0,
            };
          });
        }
      }
      setEnrollmentData(newEnrollmentData);
    }
  }

  // Handle input changes for a specific tier
  const handleEnrollmentChange = (tierIndex, field, value) => {
    const numValue = parseInt(value) || 0;

    setEnrollmentData((prev) => ({
      ...prev,
      [`tier_${tierIndex}`]: {
        ...prev[`tier_${tierIndex}`],
        [field]: numValue || 0,
      },
    }));
  };

  // Apply changes to plansData - apply tier values to all corresponding plans
  const applyChanges = () => {
    const updatedPlansData = plansData.map((dataset) => ({
      ...dataset,
      plans: dataset.plans.map((plan, planIndex) => ({
        ...plan,
        // enrollment: enrollmentData[`tier_${planIndex}`] || plan.enrollment,
        enrollment: {
          individual: enrollmentData[`tier_${planIndex}`]?.individual || 0,
          individualAndSpouse: enrollmentData[`tier_${planIndex}`]?.individualAndSpouse || 0,
          individualAndChildren: enrollmentData[`tier_${planIndex}`]?.individualAndChildren || 0,
          family: enrollmentData[`tier_${planIndex}`]?.family || 0,
        },
      })),
    }));

    setPlansData(updatedPlansData);
  };

  const getEnrollmentValue = (tierIndex, field) => {
    return enrollmentData[`tier_${tierIndex}`]?.[field] || 0;
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200 mb-6">
      <div className={`flex justify-between items-center ${isCollapsed ? "mb-0" : "mb-4"}`}>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="text-blue-600 hover:text-blue-800 focus:outline-none"
          >
            <span className="text-xl font-bold">
              {isCollapsed ? (
                <ChevronUpIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
              ) : (
                <ChevronDownIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
              )}
            </span>
          </button>
          <h2 className="text-xl font-bold text-gray-800">
            Edit Enrollment Values
          </h2>
        </div>
        {/* {!isCollapsed && ( */}
        <div className="flex space-x-2">
          <select
            className="px-3 border border-gray-300 rounded text-gray-700 bg-white hover:border-gray-400 focus:outline-none focus:border-blue-500"
            defaultValue=""
            onChange={(e) => {
              handleEnrollmentDataChange(e);
            }}
          >
            <option disabled value="" className="bg-gray-200 text-gray-500">Use enrollment values from:</option>
            {plansData.map((dataset, index) => (
              <option key={index} value={index}>
                {dataset.type.charAt(0).toUpperCase() + dataset.type.slice(1) + (dataset.type === 'alternative' ? ` ${index-1}` : '')} ({dataset.carrier})
              </option>
            ))}
            <option value="custom">Custom</option>
          </select>
          {/* {!isCollapsed && ( */}
            <button
              onClick={applyChanges}
              className={`px-4 py-2 rounded text-white font-medium bg-blue-500 hover:bg-blue-600 transition-colors`}
            >
              Apply to All Plans
            </button>
          {/* )} */}
        </div>
        {/* )} */}
      </div>

      {!isCollapsed && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-3 py-2 text-left font-medium text-gray-700">
                    Coverage Type
                  </th>
                  {Array.from({ length: maxPlans }, (_, i) => (
                    <th
                      key={i}
                      className="border border-gray-300 px-3 py-2 text-center font-medium text-gray-700"
                    >
                      Option {i + 1}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Individual Row */}
                <tr>
                  <td className="border border-gray-300 px-3 py-2 font-medium bg-gray-50">
                    Individual
                  </td>
                  {Array.from({ length: maxPlans }, (_, tierIndex) => (
                    <td
                      key={tierIndex}
                      className="border border-gray-300 px-2 py-1"
                    >
                      <input
                        type="number"
                        defaultValue={0}
                        min="0"
                        disabled={!isCustom}
                        value={getEnrollmentValue(tierIndex, "individual")}
                        onChange={(e) =>
                          handleEnrollmentChange(
                            tierIndex,
                            "individual",
                            e.target.value
                          )
                        }
                        className="w-full px-2 py-1 border border-gray-300 rounded text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </td>
                  ))}
                </tr>

                {/* Individual + Spouse Row */}
                <tr>
                  <td className="border border-gray-300 px-3 py-2 font-medium bg-gray-50">
                    Individual + Spouse
                  </td>
                  {Array.from({ length: maxPlans }, (_, tierIndex) => (
                    <td
                      key={tierIndex}
                      className="border border-gray-300 px-2 py-1"
                    >
                      <input
                        type="number"
                        defaultValue={0}
                        min="0"
                        disabled={!isCustom}
                        value={getEnrollmentValue(
                          tierIndex,
                          "individualAndSpouse"
                        )}
                        onChange={(e) =>
                          handleEnrollmentChange(
                            tierIndex,
                            "individualAndSpouse",
                            e.target.value
                          )
                        }
                        className="w-full px-2 py-1 border border-gray-300 rounded text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </td>
                  ))}
                </tr>

                {/* Individual + Children Row */}
                <tr>
                  <td className="border border-gray-300 px-3 py-2 font-medium bg-gray-50">
                    Individual + Children
                  </td>
                  {Array.from({ length: maxPlans }, (_, tierIndex) => (
                    <td
                      key={tierIndex}
                      className="border border-gray-300 px-2 py-1"
                    >
                      <input
                        type="number"
                        defaultValue={0}
                        min="0"
                        disabled={!isCustom}
                        value={getEnrollmentValue(
                          tierIndex,
                          "individualAndChildren"
                        )}
                        onChange={(e) =>
                          handleEnrollmentChange(
                            tierIndex,
                            "individualAndChildren",
                            e.target.value
                          )
                        }
                        className="w-full px-2 py-1 border border-gray-300 rounded text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </td>
                  ))}
                </tr>

                {/* Family Row */}
                <tr>
                  <td className="border border-gray-300 px-3 py-2 font-medium bg-gray-50">
                    Family
                  </td>
                  {Array.from({ length: maxPlans }, (_, tierIndex) => (
                    <td
                      key={tierIndex}
                      className="border border-gray-300 px-2 py-1"
                    >
                      <input
                        type="number"
                        defaultValue={0}
                        min="0"
                        disabled={!isCustom}
                        value={getEnrollmentValue(tierIndex, "family")}
                        onChange={(e) =>
                          handleEnrollmentChange(
                            tierIndex,
                            "family",
                            e.target.value
                          )
                        }
                        className="w-full px-2 py-1 border border-gray-300 rounded text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default EditEnrollment;
